import {Component, ElementRef, HostListener, Inject, OnInit, Optional} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router, RouterModule } from '@angular/router';

import { HeaderComponent } from './components/header/header.component';
import { FooterComponent } from './components/footer/footer.component';
import { SideMenuComponent } from './components/side-menu/side-menu.component';
import { ApprovalModalComponent } from './components/approval-modal/approval-modal.component';
import { SignatureExplanationModalComponent } from './components/signature-explanation-modal/signature-explanation-modal.component';
import { MimicPanelComponent } from './components/mimic-panel/mimic-panel.component';

import { StateService } from './services/state.service';
import { Web3Service } from './services/web3.service';
import { DataService } from './services/data.service';
import { ThemeService } from './services/theme.service';
import { MetaService } from './services/meta.service';
import { PlatformService } from './services/platform.service';
import { environment } from 'src/environments/environment';
import { isLocalDevMimicEnabled } from '@/utils/local-mimic';

import { filter, observeOn, scan, take, tap, throttleTime } from 'rxjs/operators';
import { asyncScheduler, combineLatest, fromEvent, map, Observable, shareReplay } from 'rxjs';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { isPlatformServer } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,

    HeaderComponent,
    SideMenuComponent,
    FooterComponent,
    ApprovalModalComponent,
    SignatureExplanationModalComponent,
    MimicPanelComponent,
  ],
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})

export class AppComponent implements OnInit {

  isMarketplace$!: Observable<any>;
  delegatedWalletAnnouncement$ = combineLatest([
    this.stateSvc.walletAddress$,
    this.stateSvc.authenticatedAsAddress$
  ]).pipe(
    map(([connectedWalletAddress, authenticatedAsAddress]) => {
      if (!connectedWalletAddress || !authenticatedAsAddress) {
        return null;
      }
      if (connectedWalletAddress.toLowerCase() === authenticatedAsAddress.toLowerCase()) {
        return null;
      }
      return {
        fullAddress: authenticatedAsAddress,
        shortAddress: this.getShortAddress(authenticatedAsAddress)
      };
    }),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  showInstallPrompt = false;
  promptType = 'bottom';
  installMessage = '';
  installInstructions = '';

  menuState$ = this.stateSvc.menuOpen$;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    public dataSvc: DataService, // REACTIVATED FOR TESTING
    public web3Svc: Web3Service, // REACTIVATED FOR TESTING
    public stateSvc: StateService,
    private themeSvc: ThemeService,
    private elRef: ElementRef,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
    private metaSvc: MetaService,
    private platformService: PlatformService,
    private route: ActivatedRoute,
    @Optional() private swUpdate: SwUpdate
  ) {
    console.log('[SSR] AppComponent constructor: Starting...');
    console.log('[SSR] AppComponent constructor: Platform ID:', this.platformId);
    console.log('[SSR] AppComponent constructor: Is server:', isPlatformServer(this.platformId));
    
    // Handle SwUpdate only if available and on client side
    if (this.swUpdate && !isPlatformServer(this.platformId) && this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates
        .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
        .subscribe((evt: VersionReadyEvent) => {
          const text = environment.collection === 'cryptopunks-v1' ? 'v1cryptopunks.com update available. Get the latest marketplace features.' : 
                      environment.collection === 'cryptophunks' ? ' CryptoPhunks.com update available. Get the latest marketplace features.' :
                      'CryptoPunks.eth.limo update available. Get the latest marketplace features.';
          if (confirm(text)) {
            this.platformService.reload();
          }
        });
        this.swUpdate.checkForUpdate()
    }

    const mode = this.platformService.getLocalStorageItem('mode');

    if (this.platformService.isClient) {
      this.platformService.matchMedia('(prefers-color-scheme: dark)')?.addEventListener('change', (e) => {
        this.themeSvc.setTheme(e.matches ? 'dark' : 'light');
      });
    }

    // Default to light when no saved preference (do not follow OS on first visit)
    this.themeSvc.setTheme(mode || 'light');

    this.router.events.pipe(
      ////////////////////////
      // Scroll restoration //
      ////////////////////////
      filter((event) => event instanceof NavigationStart || event instanceof NavigationEnd),
      scan((acc: any, event: any) => {
        const fromUrl = event instanceof NavigationStart ? acc.currentUrl : acc.fromUrl;
        const toUrl = event instanceof NavigationStart ? event.url : acc.toUrl;
        return {
          event,
          positions: {
            ...acc.positions,
            ...(event instanceof NavigationStart ? { [event.id]: this.platformService.pageYOffset || this.document.documentElement.scrollTop } : {}),
          },
          trigger: event instanceof NavigationStart ? event.navigationTrigger : acc.trigger,
          idToRestore: (event instanceof NavigationStart && event.restoredState && event.restoredState.navigationId + 1) || acc.idToRestore,
          fromUrl,
          toUrl,
          currentUrl: event instanceof NavigationEnd ? (event.urlAfterRedirects || event.url) : acc.currentUrl,
        };
      }),
      filter(({ event, trigger }) => event instanceof NavigationEnd && !!trigger),
      observeOn(asyncScheduler),
      tap(({ trigger, positions, idToRestore, fromUrl, toUrl }: { trigger: string, positions: any, idToRestore: number, fromUrl?: string, toUrl?: string }) => {
        this.platformService.setTimeout(() => {
          const skipScrollReset = this.isBidsDetailDeepLinkTransition(fromUrl, toUrl);
          if (trigger === 'imperative' && !skipScrollReset) this.document.documentElement.scrollTop = 0;
          if (trigger === 'popstate') this.document.documentElement.scrollTop = positions[idToRestore];
        }, 0);
      })
    ).subscribe();

    // Reset marketplace filters when navigating away from marketplace pages
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      tap((event: NavigationEnd) => {
        const currentUrl = event.url;
        const isMarketplacePage = this.isMarketplaceRoute(currentUrl);
        
        // If we're not on a marketplace page, reset the marketplace filters
        if (!isMarketplacePage) {
          this.stateSvc.resetMarketplaceFilter();
        }
      })
    ).subscribe();

    fromEvent(this.document, 'mouseup').pipe(
      tap(($event: Event) => {
        $event.stopPropagation();
        this.stateSvc.setDocumentClick($event as MouseEvent);
      })
    ).subscribe();

    if (this.platformService.isClient) {
      fromEvent(this.platformService.window!, 'resize').pipe(
        throttleTime(500),
        tap(() => this.stateSvc.setIsMobile(this.platformService.innerWidth < 801))
      ).subscribe();
    }
    
    console.log('[SSR] AppComponent constructor: Completed successfully');
  }

  @HostListener('document:keydown.escape', ['$event'])
  keydownHandler($event: Event) {
    this.stateSvc.setKeydownEscape($event as KeyboardEvent);
  }

  standalone: boolean = false;
  ngOnInit(): void {
    console.log('[SSR] AppComponent ngOnInit: Starting...');
    console.log('App Init');


    this.elRef.nativeElement.classList.add(environment.collection);

    // Handle ?mimic=0x... (read from router URL — reliable on deep links like /details/:id)
    if (this.platformService.isClient) {
      const handleMimicQueryParam = () => {
        const mimicAddr = this.readMimicQueryParam();
        if (!mimicAddr) {
          return;
        }

        const clearMimicQueryParam = () => {
          if (!this.readMimicQueryParam()) {
            return;
          }
          void this.router.navigate([], {
            queryParams: { mimic: null },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          });
        };

        const activate = () => {
          if (isLocalDevMimicEnabled()) {
            void this.web3Svc.activateMimic(mimicAddr).finally(clearMimicQueryParam);
            return;
          }

          combineLatest([
            this.stateSvc.web3Connected$,
            this.stateSvc.realWalletAddress$,
          ]).pipe(
            filter(([connected]) => connected),
            take(1),
          ).subscribe(([, realAddr]) => {
            if (this.web3Svc.isAdminWallet(realAddr)) {
              void this.web3Svc.activateMimic(mimicAddr);
            }
            clearMimicQueryParam();
          });
        };

        // Defer until after wagmi's first account event (disconnect on cold load)
        setTimeout(activate, 50);
      };

      handleMimicQueryParam();

      this.router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      ).subscribe(() => handleMimicQueryParam());
    }

    // Check if user already dismissed the prompt
    if (!!this.platformService.getLocalStorageItem('installPromptDismissed')) return;

    if (this.platformService.matchMedia('(display-mode: standalone)')?.matches) {
      this.standalone = true;
      this.showInstallPrompt = false;
      return;
    }

    // Check if it's iOS
    const isIOS = this.platformService.navigator && /iPad|iPhone|iPod/.test(this.platformService.navigator.userAgent) && !(this.platformService.window as any).MSStream;

    if (isIOS) {
      this.showInstallPrompt = true;

      this.installMessage = 'Add CryptoPunks.eth.limo to your homescreen for real-time push notifications.';
      this.installInstructions = 'To install this app on your device: Tap "Share", then "Add to Home Screen" in your default browser.';
    } else if (this.platformService.isClient) {
      this.platformService.addEventListener('beforeinstallprompt', (event) => {
        // Prevent the mini-infobar from appearing on mobile
        event.preventDefault();
        // Store the event to trigger later
        this.showInstallPrompt = true;
        this.setInstallMessageForOthers();
      });
    }
    
    console.log('[SSR] AppComponent ngOnInit: Completed successfully');
  }

  setInstallMessageForOthers() {
    // Check if it's a desktop
    const isDesktop = this.platformService.navigator && !(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(this.platformService.navigator.userAgent));

    if (isDesktop) {
      this.promptType = 'small';
      this.installMessage = 'Turn on push notifications for real-time ' + environment.collectionTitle + ' updates.';
      this.installInstructions = 'To install this app, use your browser\'s settings and "Add to Home Screen" or "Install"';
    } else {
      this.promptType = 'large';
      this.installMessage = 'Turn on push notifications for real-time ' + environment.collectionTitle + ' updates.';
      this.installInstructions = 'To install this app, tap on the browser\'s menu and "Add to Home Screen"';
    }
  }

  dismissPrompt() {
    this.showInstallPrompt = false;
    this.platformService.setLocalStorageItem('installPromptDismissed', 'true');
  }

  reloadPage() {
    // Reload the angular app
    this.platformService.reload();
  }

  private readMimicQueryParam(): string | null {
    const value = this.router.parseUrl(this.router.url).queryParamMap.get('mimic');
    return value?.trim() ? value.trim() : null;
  }

  private isMarketplaceRoute(url: string): boolean {
    // Define marketplace routes based on the routes configuration
    const marketplaceRoutes = [
      'listings',
      'sales', 
      'bids',
      'all',
      'owned',
      'nftx-vault',
      'cigclaim' // Add cigclaim to prevent marketplace filter reset
    ];
    
    // Remove query parameters and hash from URL
    const cleanUrl = url.split('?')[0].split('#')[0];
    
    // Check if the URL matches any marketplace route
    return marketplaceRoutes.some(route => cleanUrl === `/${route}` || cleanUrl === route);
  }

  private getShortAddress(address: string): string {
    if (!address || address.length < 10) return address;
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }

  private isBidsDetailDeepLinkTransition(fromUrl?: string, toUrl?: string): boolean {
    const from = this.normalizePath(fromUrl);
    const to = this.normalizePath(toUrl);
    const isFromBidsBase = from === '/bids';
    const isToBidsBase = to === '/bids';
    const isFromBidsDetail = /^\/bids\/detail\/[^/]+$/.test(from);
    const isToBidsDetail = /^\/bids\/detail\/[^/]+$/.test(to);

    return (isFromBidsBase && isToBidsDetail) || (isFromBidsDetail && isToBidsBase);
  }

  private normalizePath(url?: string): string {
    if (!url) {
      return '';
    }

    const withoutHash = url.split('#')[0] || '';
    const withoutQuery = withoutHash.split('?')[0] || '';
    return withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;
  }
}
